import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure, type PortalFailure } from "../lib/errors";

/**
 * Which notifications a person wants, per category and per channel.
 *
 * The read is an ordinary RLS-scoped select and the write is the named RPC that has existed since
 * migration 7, which is the rule `app/data/portalQueries.ts` states for the whole data layer.
 *
 * **In-app is not here and that is the design.** `public.notification_preferences` has a column for
 * push and a column for email and none for in-app, because plan.md section 5.7 makes the in-app
 * channel the permanent base channel. A switch for it would be a control that cannot do anything,
 * and the migration that created the table said so in its own comment.
 */

export const notificationPreferencesKey = (tenantId: string) =>
  ["portal", "notification-preferences", tenantId] as const;

/** The four plan.md section 5.7 calls configurable. `security` is deliberately not one of them. */
export const PREFERENCE_CATEGORIES = [
  "chat_reply",
  "request_update",
  "outage",
  "document",
  // Build round B2, 2026-10-09. On by default for push as well as e-mail, unlike the four before it.
  "report"
] as const;

export type PreferenceCategory = (typeof PREFERENCE_CATEGORIES)[number];

export interface NotificationPreference {
  category: string;
  push_enabled: boolean;
  email_enabled: boolean;
}

/**
 * What the server holds, which is not the same as what a person sees.
 *
 * A category with no row has never been touched, and the defaults are the column defaults: email on,
 * push off. The screen fills those in rather than showing a category as absent, because "we have no
 * row for this" is not an answer to "will you email me about it".
 */
export function useNotificationPreferences(
  tenantId: string | null
): UseQueryResult<NotificationPreference[]> {
  return useQuery({
    queryKey: notificationPreferencesKey(tenantId ?? "none"),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("category, push_enabled, email_enabled");

      if (error) {
        throw error;
      }

      return (data ?? []) as NotificationPreference[];
    }
  });
}

export interface UpdatePreferenceInput {
  tenantId: string;
  category: PreferenceCategory;
  pushEnabled: boolean;
  emailEnabled: boolean;
}

/** What `onMutate` hands `onError` so a refused write can be put back exactly as it was. */
interface PreferenceRollback {
  previous: NotificationPreference[] | undefined;
}

/**
 * One category, both channels, one call.
 *
 * The gateway takes both because the row holds both, so a screen that sent only the changed one
 * would have to read the other first and could send a value it had not seen change. Passing what is
 * on screen is what makes the write say exactly what the person is looking at.
 *
 * **The cache is written before the server answers, and put back if the server refuses.** The panel
 * reads `checked` from this cache, so without the write below a click was rolled back the instant it
 * was made and the box only moved once invalidation had refetched the row. On a slow connection that
 * looks exactly like a click the portal never received, and the person clicks again. The optimistic
 * write is not a cosmetic nicety here; it is what stops a control from denying an action it has in
 * fact accepted.
 *
 * `onError` restores the rows that were there before the click rather than flipping the box back,
 * because several categories can be in flight at once and the state to return to is the whole list
 * as it stood. `onSettled` invalidates on both paths, so the server always has the last word.
 */
export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();

  return useMutation<void, PortalFailure, UpdatePreferenceInput, PreferenceRollback>({
    mutationFn: async ({ tenantId, category, pushEnabled, emailEnabled }) => {
      const supabase = getSupabaseBrowserClient();

      const { error } = await supabase.rpc("update_notification_preference", {
        p_tenant_id: tenantId,
        p_category: category,
        p_push_enabled: pushEnabled,
        p_email_enabled: emailEnabled
      });

      if (error) {
        throw toPortalFailure(error);
      }
    },
    onMutate: async (variables) => {
      const key = notificationPreferencesKey(variables.tenantId);

      // An in-flight read would land after this write and put the old value back on screen.
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<NotificationPreference[]>(key);

      queryClient.setQueryData<NotificationPreference[]>(key, (current) => {
        const existing = current ?? [];
        const written: NotificationPreference = {
          category: variables.category,
          push_enabled: variables.pushEnabled,
          email_enabled: variables.emailEnabled
        };

        // A category the person has never touched has no row at all, and the panel has been showing
        // it as the column defaults. The optimistic write has to add the row rather than only map
        // over the existing ones, or the first click on such a category would change nothing.
        return existing.some((row) => row.category === variables.category)
          ? existing.map((row) => (row.category === variables.category ? written : row))
          : [...existing, written];
      });

      return { previous };
    },
    onError: (_error, variables, context) => {
      // Undefined means the query had no cached answer when the click happened, and there is
      // nothing to put back. Writing undefined into the cache would be a different change.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(notificationPreferencesKey(variables.tenantId), context.previous);
      }
    },
    onSettled: (_result, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: notificationPreferencesKey(variables.tenantId)
      });
    }
  });
}

/** The stored row for a category, or the column defaults when there is none. */
export function preferenceFor(
  rows: NotificationPreference[] | undefined,
  category: PreferenceCategory
): { pushEnabled: boolean; emailEnabled: boolean } {
  const row = (rows ?? []).find((entry) => entry.category === category);

  return {
    // An untouched row is on for push only for a report (private.push_delivery_wanted says the same).
    pushEnabled: row?.push_enabled ?? category === "report",
    emailEnabled: row?.email_enabled ?? true
  };
}
